import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  calculateLongFormMeansTest,
  createDefaultLongFormData,
  longFormMoney,
  mergeLongFormData,
  suggestedPriorityClaimTotal,
  suggestedRentHousingAdjustment,
  suggestedSecuredDebts,
} from '../../utils/longFormMeansTest';
import { loadMeansTestStandards } from '../../utils/meansTestStandards';
import {
  OfficialLongFormLineHelp,
  OfficialLongFormLineLabel,
} from './OfficialLongFormLineHelp';

const API_URL = process.env.REACT_APP_API_URL;

const OTHER_NECESSARY = [
  ['taxes', '16. Taxes'],
  ['involuntary_deductions', '17. Involuntary payroll deductions'],
  ['life_insurance', '18. Term life insurance'],
  ['court_ordered_payments', '19. Court-ordered payments'],
  ['education', '20. Required education'],
  ['childcare', '21. Childcare'],
  ['additional_health_care', '22. Health care above the standard'],
  ['optional_telephones', '23. Necessary optional telephone services'],
];

const ADDITIONAL_DEDUCTIONS = [
  ['health_insurance', '25a. Health insurance'],
  ['disability_insurance', '25b. Disability insurance'],
  ['health_savings_account', '25c. Health savings account'],
  ['family_support_contributions', '26. Care of household or family members'],
  ['family_violence', '27. Protection against family violence'],
  ['additional_home_energy', '28. Additional home energy costs'],
  ['dependent_education', '29. Education for dependent children under 18'],
  ['additional_food_clothing', '30. Additional food and clothing'],
  ['charitable_contributions', '31. Continuing charitable contributions'],
];

const newSecuredDebt = () => ({
  category: 'other',
  creditor_name: '',
  property_description: '',
  average_monthly_payment: 0,
  includes_taxes_insurance: false,
  cure_total: 0,
});

const newSpecialCircumstance = () => ({ description: '', amount: 0 });
const newIncomeExpenseChange = () => ({
  form: '122C-1', line: '', reason: '', date: '', direction: 'increase', amount: 0,
});

const syncAutomaticRentAdjustment = ({ data, caseData, dependents, expenses }) => {
  if (data.housing_adjustment_source !== 'rent_difference') return data;
  const suggestion = suggestedRentHousingAdjustment({
    caseData,
    dependents,
    expenses,
    securedDebts: data.secured_debts || [],
    householdSize: data.household_size,
  });
  return {
    ...data,
    housing_adjustment_amount: suggestion.amount,
    housing_adjustment_explanation: suggestion.explanation,
    housing_adjustment_source: 'rent_difference',
  };
};

function MoneyInput({ chapter, line, label, value, onChange, help }) {
  const inferredLine = line || String(label).match(/(?:line\s*)?(\d+[a-z]?)/i)?.[1];
  return (
    <label className="block">
      <OfficialLongFormLineLabel
        chapter={chapter}
        line={inferredLine}
        className="text-sm font-medium text-gray-700"
      >
        {label}
      </OfficialLongFormLineLabel>
      <div className="mt-1 flex rounded-md shadow-sm">
        <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">$</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={value ?? ''}
          onChange={event => onChange(event.target.value)}
          className="min-w-0 flex-1 rounded-r-md border border-gray-300 px-3 py-2"
        />
      </div>
      {help && <span className="mt-1 block text-xs text-gray-500">{help}</span>}
    </label>
  );
}

function LineValue({ chapter, line, children, tone = 'gray' }) {
  const toneClass = tone === 'green'
    ? 'bg-green-50 text-green-900'
    : tone === 'indigo' ? 'bg-indigo-50 text-indigo-900' : 'bg-gray-50 text-gray-900';
  return (
    <div className={`rounded-md p-3 text-sm ${toneClass}`}>
      <OfficialLongFormLineHelp chapter={chapter} line={line} />
      <span className="ml-0.5">{children}</span>
    </div>
  );
}

function FormSection({ title, children, open = false }) {
  return (
    <details open={open} className="rounded-lg border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none px-4 py-3 font-semibold text-gray-800">
        {title}
      </summary>
      <div className="border-t border-gray-200 p-4">{children}</div>
    </details>
  );
}

export default function LongFormMeansTestSection({
  caseId,
  caseData,
  dependents = [],
  expenses = [],
  creditors = [],
  meansTestIncomes = [],
  maritalAdjustments = [],
  ready = false,
  onChange,
  onMaritalAdjustmentsChange,
}) {
  const token = localStorage.getItem('accessToken');
  const [standardsRevision, setStandardsRevision] = useState(0);
  const defaults = useMemo(() => createDefaultLongFormData({
    caseData, dependents, expenses, creditors, standardsRevision,
  }), [caseData, dependents, expenses, creditors, standardsRevision]);
  const [formData, setFormData] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [standardsLoading, setStandardsLoading] = useState(true);
  const [standardsWarning, setStandardsWarning] = useState('');
  const [maritalRows, setMaritalRows] = useState(maritalAdjustments);
  const [newMaritalAdjustment, setNewMaritalAdjustment] = useState({ description: '', amount: '' });
  const [refreshingSchedule, setRefreshingSchedule] = useState('');
  const latestCreditorsRef = useRef(creditors);

  useEffect(() => {
    latestCreditorsRef.current = creditors;
  }, [creditors]);

  useEffect(() => {
    setMaritalRows(maritalAdjustments);
  }, [maritalAdjustments]);

  useEffect(() => {
    let mounted = true;
    loadMeansTestStandards()
      .then(() => {
        if (mounted) {
          setStandardsWarning('');
          setStandardsRevision(current => current + 1);
        }
      })
      .catch(error => {
        if (!mounted) return;
        setStandardsWarning(
          `Could not load the active means-test standards. Using the most recently cached or bundled values. ${error.message || ''}`.trim()
        );
      })
      .finally(() => {
        if (mounted) setStandardsLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!ready || !caseData) return undefined;
    let mounted = true;
    setLoading(true);

    fetch(`${API_URL}/case/${caseId}/means-test/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async response => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.detail || 'Could not load the long-form means test.');
        return payload;
      })
      .then(payload => {
        if (!mounted) return;
        const merged = mergeLongFormData(payload.long_form_data, defaults);
        const synced = syncAutomaticRentAdjustment({
          data: merged,
          caseData,
          dependents,
          expenses,
        });
        setFormData(synced);
        onChange?.(synced);
        setMessage('');
      })
      .catch(error => {
        if (!mounted) return;
        setFormData(defaults);
        onChange?.(defaults);
        setMessage(error.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, [caseId, caseData, defaults, dependents, expenses, onChange, ready, token]);

  const calculation = useMemo(() => calculateLongFormMeansTest({
    caseData,
    dependents,
    meansTestIncomes,
    maritalAdjustments: maritalRows,
    longFormData: formData,
    standardsRevision,
  }), [caseData, dependents, meansTestIncomes, maritalRows, formData, standardsRevision]);

  const rentAdjustmentSuggestion = useMemo(() => suggestedRentHousingAdjustment({
    caseData,
    dependents,
    expenses,
    securedDebts: formData.secured_debts || [],
    householdSize: formData.household_size,
    standardsRevision,
  }), [caseData, dependents, expenses, formData.household_size, formData.secured_debts, standardsRevision]);

  const replaceData = next => {
    setFormData(next);
    onChange?.(next);
    setMessage('');
  };

  const restoreSuggestedValues = () => replaceData(createDefaultLongFormData({
    caseData,
    dependents,
    expenses,
    creditors: latestCreditorsRef.current,
  }));

  const update = (key, value) => replaceData({ ...formData, [key]: value });
  const updateHousingAdjustment = (key, value) => replaceData({
    ...formData,
    [key]: value,
    housing_adjustment_source: 'manual',
  });
  const useActualRentDifference = () => replaceData({
    ...formData,
    housing_adjustment_amount: rentAdjustmentSuggestion.amount,
    housing_adjustment_explanation: rentAdjustmentSuggestion.explanation,
    housing_adjustment_source: 'rent_difference',
  });
  const updateHouseholdSize = value => replaceData(syncAutomaticRentAdjustment({
    data: { ...formData, household_size: value },
    caseData,
    dependents,
    expenses,
  }));
  const updateRow = (key, index, values) => update(
    key,
    (formData[key] || []).map((row, rowIndex) => rowIndex === index ? { ...row, ...values } : row)
  );
  const deleteRow = (key, index) => update(key, (formData[key] || []).filter((_, rowIndex) => rowIndex !== index));

  const updateSecuredDebtRow = (index, values) => {
    const rows = (formData.secured_debts || []).map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...values } : row
    );
    let vehicleCount = Number(formData.vehicle_count) || 0;
    if (values.category === 'vehicle1') vehicleCount = Math.max(vehicleCount, 1);
    if (values.category === 'vehicle2') vehicleCount = 2;
    replaceData(syncAutomaticRentAdjustment({
      data: { ...formData, secured_debts: rows, vehicle_count: vehicleCount },
      caseData,
      dependents,
      expenses,
    }));
  };

  const deleteSecuredDebtRow = index => {
    const rows = (formData.secured_debts || []).filter((_, rowIndex) => rowIndex !== index);
    replaceData(syncAutomaticRentAdjustment({
      data: { ...formData, secured_debts: rows },
      caseData,
      dependents,
      expenses,
    }));
  };

  const replaceMaritalRows = rows => {
    setMaritalRows(rows);
    onMaritalAdjustmentsChange?.(rows);
    setMessage('');
  };

  const updateMaritalRow = (index, values) => replaceMaritalRows(
    maritalRows.map((row, rowIndex) => rowIndex === index ? { ...row, ...values } : row)
  );

  const saveMaritalRow = async row => {
    if (!row?.id) return;
    const response = await fetch(`${API_URL}/case/${caseId}/means-test/marital-adjustments/${row.id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        description: row.description || 'Marital adjustment',
        amount: Number(row.amount) || 0,
      }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.detail || 'Could not save the marital adjustment.');
    }
  };

  const addMaritalAdjustment = async () => {
    if (!newMaritalAdjustment.description.trim()) {
      setMessage('Enter a description for the marital adjustment.');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/case/${caseId}/means-test/marital-adjustments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          description: newMaritalAdjustment.description.trim(),
          amount: Number(newMaritalAdjustment.amount) || 0,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Could not add the marital adjustment.');
      replaceMaritalRows([...maritalRows, payload]);
      setNewMaritalAdjustment({ description: '', amount: '' });
      setMessage('Marital adjustment added.');
    } catch (error) {
      setMessage(error.message);
    }
  };

  const deleteMaritalAdjustment = async (index, row) => {
    try {
      if (row.id) {
        const response = await fetch(`${API_URL}/case/${caseId}/means-test/marital-adjustments/${row.id}/`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Could not remove the marital adjustment.');
      }
      replaceMaritalRows(maritalRows.filter((_, rowIndex) => rowIndex !== index));
    } catch (error) {
      setMessage(error.message);
    }
  };

  const fetchCurrentCreditors = async () => {
    const response = await fetch(`${API_URL}/case/${caseId}/creditors/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const payload = await response.json().catch(() => []);
    if (!response.ok) throw new Error(payload.detail || 'Could not refresh creditors.');
    latestCreditorsRef.current = payload;
    return payload;
  };

  const addMissingScheduleD = async () => {
    setRefreshingSchedule('D');
    try {
      const currentCreditors = await fetchCurrentCreditors();
      const suggestions = suggestedSecuredDebts(caseData, currentCreditors);
      const existingIds = new Set((formData.secured_debts || [])
        .map(row => String(row.source_creditor_id || ''))
        .filter(Boolean));
      const additions = suggestions.filter(row => !existingIds.has(String(row.source_creditor_id)));
      const nextRows = [...(formData.secured_debts || []), ...additions];
      const importedVehicleCount = nextRows.some(row => row.category === 'vehicle2')
        ? 2
        : nextRows.some(row => row.category === 'vehicle1') ? 1 : 0;
      replaceData(syncAutomaticRentAdjustment({
        data: {
          ...formData,
          secured_debts: nextRows,
          vehicle_count: Math.max(Number(formData.vehicle_count) || 0, importedVehicleCount),
        },
        caseData,
        dependents,
        expenses,
      }));
      setMessage(additions.length
        ? `Added ${additions.length} missing Schedule D creditor${additions.length === 1 ? '' : 's'}. Review the payment amounts before filing.`
        : 'All current Schedule D creditors are already included.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRefreshingSchedule('');
    }
  };

  const loadScheduleEPriorityClaims = async () => {
    setRefreshingSchedule('E');
    try {
      const currentCreditors = await fetchCurrentCreditors();
      const total = suggestedPriorityClaimTotal(currentCreditors);
      update('priority_claim_total', total);
      setMessage(`Loaded $${longFormMoney(total)} in priority claims from Schedule E.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRefreshingSchedule('');
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await Promise.all(maritalRows.filter(row => row.id).map(saveMaritalRow));
      const response = await fetch(`${API_URL}/case/${caseId}/means-test/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ long_form_data: formData }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Could not save the long-form means test.');
      setMessage('Long-form means test saved.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || loading || standardsLoading) {
    return <div className="mx-4 mt-8 text-center text-gray-600 sm:mx-auto sm:max-w-[1200px]">Loading long-form means test…</div>;
  }

  const chapter7 = String(caseData?.chapter) === '7';
  const aboveMedianBeforeMaritalAdjustment = calculation.combinedMonthly * 12 > calculation.medianIncome;
  if (!calculation.aboveMedian && !(chapter7 && aboveMedianBeforeMaritalAdjustment)) return null;

  const specialRows = formData.special_circumstances || [];
  const changeRows = formData.income_expense_changes || [];
  const successMessage = /saved|added|already included|loaded \$/i.test(message);
  const line40LowerLabel = `$${Number(calculation.line40LowerLimit).toLocaleString('en-US')}`;
  const line40UpperLabel = `$${Number(calculation.line40UpperLimit).toLocaleString('en-US')}`;
  const line40OptionClass = category => calculation.chapter7Line40Category === category
    ? 'border-indigo-500 bg-indigo-50 text-indigo-950 ring-1 ring-indigo-300'
    : 'border-gray-200 bg-white text-gray-600';

  const chapter7ResultExplanation = (() => {
    if (calculation.chapter7Line40Category === 'below_range') {
      return `Line 39d is below ${line40LowerLabel}. The first line 40 box and page 1 box 1 will be checked. Lines 41 and 42 are not used.`;
    }
    if (calculation.chapter7Line40Category === 'above_range') {
      return `Line 39d is above ${line40UpperLabel}. The second line 40 box and page 1 box 2 will be checked. Line 42 is not used; Part 4 may be completed if special circumstances are claimed.`;
    }
    if (calculation.chapter7Line42Result === 'presumption') {
      return 'Line 40 requires the 25% test. Line 39d is equal to or more than line 41b, so the second line 42 box and page 1 box 2 will be checked. Part 4 may be completed if special circumstances are claimed.';
    }
    return 'Line 40 requires the 25% test. Line 39d is less than line 41b, so the first line 42 box and page 1 box 1 will be checked.';
  })();

  return (
    <div className="mx-4 mt-8 rounded-lg bg-slate-50 p-6 shadow-2xl sm:mx-auto sm:max-w-[1200px]">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-3xl font-bold text-gray-800">
            {chapter7 ? 'Form 122A-2 Long-Form Means Test' : 'Form 122C-2 Disposable Income'}
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            {calculation.aboveMedian
              ? `Adjusted current monthly income of $${longFormMoney(calculation.adjustedMonthlyIncome)} annualizes above the $${longFormMoney(calculation.medianIncome)} median. Standard allowances are filled automatically; review and complete the remaining fields below.`
              : `The line 3 marital adjustments reduce annualized current monthly income below the $${longFormMoney(calculation.medianIncome)} median. Save the adjustments, but Form 122A-2 will not be added to the generated packet while the case remains below median.`}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={restoreSuggestedValues}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Restore suggested values
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save long form'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`mt-4 rounded-md border px-4 py-3 text-sm ${successMessage ? 'border-green-300 bg-green-50 text-green-800' : 'border-red-300 bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      {standardsWarning && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {standardsWarning}
        </div>
      )}

      <div className="mt-5 grid gap-4">
        {chapter7 && (
          <FormSection title="Chapter 7 marital adjustments - line 3" open>
            <p className="mb-2 text-sm text-gray-600">
              <OfficialLongFormLineHelp chapter="7" line="2" /> Line 2 determines whether a marital adjustment may be used based on Form 122A-1 Column B and whether the spouse is filing.
            </p>
            <p className="mb-4 text-sm text-gray-600">
              <OfficialLongFormLineHelp chapter="7" line="3" />{' '}
              If a nonfiling spouse's income is included on Form 122A-1, list the portions not used for the debtor's household or dependents. These amounts reduce adjusted current monthly income on line 3 before the Chapter 7 deduction calculation.
            </p>
            <div className="space-y-2">
              {maritalRows.map((row, index) => (
                <div key={row.id || index} className="grid gap-2 md:grid-cols-[1fr_180px_auto]">
                  <input
                    value={row.description || ''}
                    onChange={event => updateMaritalRow(index, { description: event.target.value })}
                    onBlur={() => saveMaritalRow(row).catch(error => setMessage(error.message))}
                    placeholder="Example: nonfiling spouse's separate credit-card payment"
                    className="rounded border border-gray-300 px-3 py-2"
                  />
                  <div className="flex rounded-md shadow-sm">
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.amount ?? ''}
                      onChange={event => updateMaritalRow(index, { amount: event.target.value })}
                      onBlur={() => saveMaritalRow(row).catch(error => setMessage(error.message))}
                      className="min-w-0 flex-1 rounded-r-md border border-gray-300 px-3 py-2"
                    />
                  </div>
                  <button type="button" onClick={() => deleteMaritalAdjustment(index, row)} className="text-left text-red-600">Remove</button>
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-[1fr_180px_auto]">
              <input
                value={newMaritalAdjustment.description}
                onChange={event => setNewMaritalAdjustment(previous => ({ ...previous, description: event.target.value }))}
                placeholder="New marital-adjustment description"
                className="rounded border border-gray-300 px-3 py-2"
              />
              <div className="flex rounded-md shadow-sm">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newMaritalAdjustment.amount}
                  onChange={event => setNewMaritalAdjustment(previous => ({ ...previous, amount: event.target.value }))}
                  className="min-w-0 flex-1 rounded-r-md border border-gray-300 px-3 py-2"
                />
              </div>
              <button type="button" onClick={addMaritalAdjustment} className="rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700">Add deduction</button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <LineValue chapter="7" line="1"><strong>Line 1 current monthly income:</strong><br />${longFormMoney(calculation.combinedMonthly)}</LineValue>
              <LineValue chapter="7" line="3"><strong>Line 3 deductions:</strong><br />-${longFormMoney(calculation.maritalAdjustment)}</LineValue>
              <LineValue chapter="7" line="4" tone="indigo"><strong>Line 4 adjusted monthly income:</strong><br />${longFormMoney(calculation.adjustedMonthlyIncome)}</LineValue>
            </div>
          </FormSection>
        )}

        <FormSection title="IRS standards and household information" open>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="block">
              <OfficialLongFormLineLabel chapter={caseData?.chapter} line="5" className="text-sm font-medium text-gray-700">
                Line 5 - People used for deductions
              </OfficialLongFormLineLabel>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.household_size ?? calculation.householdSize}
                onChange={event => updateHouseholdSize(event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="block">
              <OfficialLongFormLineLabel chapter={caseData?.chapter} line="11" className="text-sm font-medium text-gray-700">
                Line 11 - Vehicles used for standards
              </OfficialLongFormLineLabel>
              <select
                value={formData.vehicle_count ?? calculation.inferredVehicleCount}
                onChange={event => update('vehicle_count', event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              >
                <option value="0">0 vehicles</option>
                <option value="1">1 vehicle</option>
                <option value="2">2 vehicles</option>
              </select>
              <span className="mt-1 block text-xs text-gray-500">
                The case currently lists {calculation.inferredVehicleCount}. Override this for a nonfiling spouse's vehicle.
              </span>
            </label>
            <LineValue chapter={caseData?.chapter} line="6"><strong>Line 6:</strong> ${longFormMoney(calculation.line6)} food, clothing, and other</LineValue>
            <LineValue chapter={caseData?.chapter} line="7"><strong>Line 7:</strong> ${longFormMoney(calculation.line7g)} health allowance</LineValue>
            <LineValue chapter={caseData?.chapter} line="8"><strong>Line 8:</strong> ${longFormMoney(calculation.line8)} housing operating</LineValue>
            <LineValue chapter={caseData?.chapter} line="12"><strong>Line 12:</strong> ${longFormMoney(calculation.line12)} vehicle operation</LineValue>
            <LineValue chapter={caseData?.chapter} line="14"><strong>Line 14:</strong> ${longFormMoney(calculation.line14)} public transportation</LineValue>
            <div className="rounded-md bg-gray-50 p-3 text-sm"><strong>District multiplier:</strong> {longFormMoney(calculation.adminMultiplier)}%</div>
          </div>
          {calculation.vehicleCountRaisedByDebt && (
            <p className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              The form is using 2 vehicles because a secured-debt row is assigned to Vehicle 2. This activates both the regional Vehicle 2 operating/ownership standards and lines 13d-13f.
            </p>
          )}
          <div className="mt-4 overflow-x-auto rounded-md border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Official-form calculation</th>
                  <th className="px-3 py-2 text-right">IRS standard</th>
                  <th className="px-3 py-2 text-right">Schedule D payment</th>
                  <th className="px-3 py-2 text-right">Net deduction</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-3 py-2"><strong>Home:</strong> lines 9a, 9b, and 9c</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="9a" /> ${longFormMoney(calculation.line9a)}</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="9b" /> ${longFormMoney(calculation.line9b)}</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="9c" /> ${longFormMoney(calculation.line9c)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2"><strong>Vehicle 1:</strong> lines 13a, 13b, and 13c</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="13a" /> ${longFormMoney(calculation.line13a)}</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="13b" /> ${longFormMoney(calculation.line13b)}</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="13c" /> ${longFormMoney(calculation.line13c)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-2"><strong>Vehicle 2:</strong> lines 13d, 13e, and 13f</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="13d" /> ${longFormMoney(calculation.line13d)}</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="13e" /> ${longFormMoney(calculation.line13e)}</td>
                  <td className="px-3 py-2 text-right"><OfficialLongFormLineHelp chapter={caseData?.chapter} line="13f" /> ${longFormMoney(calculation.line13f)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-600">
            Lines 9b, 13b, and 13e come from the secured-debt rows below. The same payments are repeated on line 33 because the official form requires them there; they are not counted twice in the total deduction.
            A vehicle ownership allowance on line 13 is claimed only when that vehicle has a loan payment entered below; the line 12 operating standard can still use the overridden vehicle count.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <MoneyInput
              chapter={caseData?.chapter}
              line="10"
              label="Line 10 - Additional housing adjustment"
              value={formData.housing_adjustment_amount}
              onChange={value => updateHousingAdjustment('housing_adjustment_amount', value)}
              help={formData.housing_adjustment_source === 'rent_difference'
                ? 'Automatically calculated from Schedule J rent when there is no home mortgage below.'
                : 'Manual override. Use the button below to return to the Schedule J rent calculation.'}
            />
            <label className="block">
              <OfficialLongFormLineLabel chapter={caseData?.chapter} line="10" className="text-sm font-medium text-gray-700">Line 10 explanation</OfficialLongFormLineLabel>
              <textarea value={formData.housing_adjustment_explanation || ''} onChange={event => updateHousingAdjustment('housing_adjustment_explanation', event.target.value)} rows={3} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900 md:col-span-2">
              {rentAdjustmentSuggestion.hasHomeMortgage ? (
                <p>A home mortgage is assigned in the secured-debt section, so the automatic rent adjustment is not applied. The home payment is handled through lines 9b and 9c.</p>
              ) : rentAdjustmentSuggestion.actualRent > 0 ? (
                <p>
                  Schedule J rent of <strong>${longFormMoney(rentAdjustmentSuggestion.actualRent)}</strong>
                  {' '}minus the line 9a IRS mortgage/rent standard of <strong>${longFormMoney(rentAdjustmentSuggestion.mortgageRentStandard)}</strong>
                  {' '}produces a line 10 adjustment of <strong>${longFormMoney(rentAdjustmentSuggestion.amount)}</strong>.
                </p>
              ) : (
                <p>No Schedule J rent amount is currently available for the automatic line 10 calculation.</p>
              )}
              <button
                type="button"
                disabled={rentAdjustmentSuggestion.hasHomeMortgage}
                onClick={useActualRentDifference}
                className="mt-3 rounded-md border border-indigo-300 bg-white px-3 py-2 font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {formData.housing_adjustment_source === 'rent_difference'
                  ? 'Recalculate actual rent difference'
                  : 'Use actual rent difference'}
              </button>
            </div>
            <MoneyInput
              chapter={caseData?.chapter}
              line="15"
              label="Line 15 - Additional public transportation"
              value={formData.additional_public_transportation}
              onChange={value => update('additional_public_transportation', value)}
              help={`Capped on the form at the current $${longFormMoney(calculation.transportation.publicTransportation)} public-transportation standard.`}
            />
          </div>
        </FormSection>

        <FormSection title="Other necessary expenses - lines 16 through 24">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {OTHER_NECESSARY.map(([key, label]) => (
              <MoneyInput chapter={caseData?.chapter} key={key} label={label} value={formData[key]} onChange={value => update(key, value)} />
            ))}
          </div>
          <p className="mt-4 rounded-md bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <OfficialLongFormLineHelp chapter={caseData?.chapter} line="24" /> Calculated line 24 total: <strong>${longFormMoney(calculation.line24)}</strong>
          </p>
        </FormSection>

        <FormSection title="Additional expense deductions - lines 25 through 32">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {ADDITIONAL_DEDUCTIONS.map(([key, label]) => (
              <MoneyInput
                chapter={caseData?.chapter}
                key={key}
                label={label}
                value={formData[key]}
                onChange={value => update(key, value)}
                help={key === 'additional_food_clothing' ? `Maximum currently calculated as $${longFormMoney(calculation.line30Maximum)}.` : undefined}
              />
            ))}
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={formData.actually_spend_health_total !== false}
              onChange={event => update('actually_spend_health_total', event.target.checked)}
            />
            The debtor actually spends the full line 25 insurance/HSA total
            <OfficialLongFormLineHelp chapter={caseData?.chapter} line="25" />
          </label>
          {formData.actually_spend_health_total === false && (
            <div className="mt-3 max-w-sm">
              <MoneyInput chapter={caseData?.chapter} line="25" label="Actual line 25 amount spent" value={formData.actual_health_total} onChange={value => update('actual_health_total', value)} />
            </div>
          )}
          <p className="mt-4 rounded-md bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <OfficialLongFormLineHelp chapter={caseData?.chapter} line="32" /> Calculated line 32 total: <strong>${longFormMoney(calculation.line32)}</strong>
          </p>
        </FormSection>

        <FormSection title="Secured debt, cure amounts, and priority claims - lines 33 through 38">
          <p className="mb-4 text-sm text-gray-600">
            Assign each Schedule D payment to its official-form category. Home payments feed both lines 9b and 33a; Vehicle 1 payments feed lines 13b and 33b; Vehicle 2 payments feed lines 13e and 33c. Only "Other secured debt" is entered directly on line 33d.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left">Form category</th>
                  <th className="px-2 py-2 text-left">Creditor</th>
                  <th className="px-2 py-2 text-left">Property</th>
                  <th className="px-2 py-2 text-left"><OfficialLongFormLineLabel chapter={caseData?.chapter} line="33">60-month average</OfficialLongFormLineLabel></th>
                  <th className="px-2 py-2 text-left">PITI</th>
                  <th className="px-2 py-2 text-left"><OfficialLongFormLineLabel chapter={caseData?.chapter} line="34">Total cure</OfficialLongFormLineLabel></th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {(formData.secured_debts || []).map((row, index) => (
                  <tr key={row.source_creditor_id || index}>
                    <td className="px-2 py-2">
                      <select value={row.category || 'other'} onChange={event => updateSecuredDebtRow(index, { category: event.target.value })} className="rounded border border-gray-300 px-2 py-1">
                        <option value="home">Home - lines 9b / 33a</option>
                        <option value="vehicle1">Vehicle 1 - lines 13b / 33b</option>
                        <option value="vehicle2">Vehicle 2 - lines 13e / 33c</option>
                        <option value="other">Other secured - line 33d</option>
                      </select>
                    </td>
                    <td className="px-2 py-2"><input value={row.creditor_name || ''} onChange={event => updateSecuredDebtRow(index, { creditor_name: event.target.value })} className="w-44 rounded border border-gray-300 px-2 py-1" /></td>
                    <td className="px-2 py-2"><input value={row.property_description || ''} onChange={event => updateSecuredDebtRow(index, { property_description: event.target.value })} className="w-44 rounded border border-gray-300 px-2 py-1" /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.01" value={row.average_monthly_payment ?? ''} onChange={event => updateSecuredDebtRow(index, { average_monthly_payment: event.target.value })} className="w-28 rounded border border-gray-300 px-2 py-1" /></td>
                    <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!row.includes_taxes_insurance} onChange={event => updateSecuredDebtRow(index, { includes_taxes_insurance: event.target.checked })} /></td>
                    <td className="px-2 py-2"><input type="number" min="0" step="0.01" value={row.cure_total ?? ''} onChange={event => updateSecuredDebtRow(index, { cure_total: event.target.value })} className="w-28 rounded border border-gray-300 px-2 py-1" /></td>
                    <td className="px-2 py-2"><button type="button" onClick={() => deleteSecuredDebtRow(index)} className="text-red-600">Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={() => update('secured_debts', [...(formData.secured_debts || []), newSecuredDebt()])} className="rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700">Add secured debt manually</button>
            <button type="button" disabled={!!refreshingSchedule} onClick={addMissingScheduleD} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {refreshingSchedule === 'D' ? 'Checking Schedule D…' : 'Add missing creditors from Schedule D'}
            </button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <LineValue chapter={caseData?.chapter} line="33a"><strong>33a - Home:</strong><br />${longFormMoney(calculation.line9b)}</LineValue>
            <LineValue chapter={caseData?.chapter} line="33b"><strong>33b - Vehicle 1:</strong><br />${longFormMoney(calculation.line13b)}</LineValue>
            <LineValue chapter={caseData?.chapter} line="33c"><strong>33c - Vehicle 2:</strong><br />${longFormMoney(calculation.line13e)}</LineValue>
            <LineValue chapter={caseData?.chapter} line="33d"><strong>33d - Other secured:</strong><br />${longFormMoney(calculation.line33d)}</LineValue>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <div>
              <MoneyInput chapter={caseData?.chapter} line="35" label="Line 35 - Total past-due priority claims" value={formData.priority_claim_total} onChange={value => update('priority_claim_total', value)} />
              <button type="button" disabled={!!refreshingSchedule} onClick={loadScheduleEPriorityClaims} className="mt-2 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700 disabled:opacity-60">
                {refreshingSchedule === 'E' ? 'Loading Schedule E…' : 'Load total from Schedule E'}
              </button>
            </div>
            <MoneyInput chapter={caseData?.chapter} line="36" label="Line 36 - Projected Chapter 13 plan payment" value={formData.projected_chapter_13_payment} onChange={value => update('projected_chapter_13_payment', value)} />
            {chapter7 && (
              <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
                <input type="checkbox" checked={formData.eligible_for_chapter_13 !== false} onChange={event => update('eligible_for_chapter_13', event.target.checked)} />
                Eligible to file Chapter 13
                <OfficialLongFormLineHelp chapter="7" line="36" />
              </label>
            )}
          </div>
          <p className="mt-4 rounded-md bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <OfficialLongFormLineHelp chapter={caseData?.chapter} line="33e" /> Line 33 secured payments: <strong>${longFormMoney(calculation.line33e)}</strong>;
            {' '}<OfficialLongFormLineHelp chapter={caseData?.chapter} line="34" /> line 34 cure allowance: <strong>${longFormMoney(calculation.line34)}</strong>;
            {' '}<OfficialLongFormLineHelp chapter={caseData?.chapter} line="37" /> line 37 debt deductions: <strong>${longFormMoney(calculation.line37)}</strong>;
            {' '}<OfficialLongFormLineHelp chapter={caseData?.chapter} line="38" /> line 38 total deductions: <strong>${longFormMoney(calculation.line38)}</strong>.
          </p>
        </FormSection>

        {chapter7 ? (
          <FormSection title="Chapter 7 presumption calculation and special circumstances">
            <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-1">
                <h4 className="font-semibold text-gray-900">Line 40 - Presumption threshold test</h4>
                <OfficialLongFormLineHelp chapter="7" line="40" />
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Compare line 39d of <strong>${longFormMoney(calculation.line39d)}</strong> with the current official-form range.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div className={`rounded-md border p-3 text-sm ${line40OptionClass('below_range')}`}>
                  <strong>Below {line40LowerLabel}</strong>
                  <p className="mt-1">No presumption. Skip lines 41 and 42.</p>
                </div>
                <div className={`rounded-md border p-3 text-sm ${line40OptionClass('within_range')}`}>
                  <strong>{line40LowerLabel} through {line40UpperLabel}</strong>
                  <p className="mt-1">Complete the line 41 25% calculation and select the result on line 42.</p>
                </div>
                <div className={`rounded-md border p-3 text-sm ${line40OptionClass('above_range')}`}>
                  <strong>Above {line40UpperLabel}</strong>
                  <p className="mt-1">Presumption of abuse. Use Part 4 if special circumstances are claimed.</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                These thresholds apply to cases filed on or after April 1, 2025 and are next subject to adjustment April 1, 2028.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <MoneyInput chapter="7" line="41a" label="Line 41a - Total nonpriority unsecured debt" value={formData.unsecured_nonpriority_total} onChange={value => update('unsecured_nonpriority_total', value)} />
              <div className={`rounded-md p-4 text-sm ${calculation.chapter7Presumption ? 'bg-red-50 text-red-900' : 'bg-green-50 text-green-900'}`}>
                <strong>{calculation.chapter7Presumption ? 'Presumption of abuse' : 'No presumption of abuse'}</strong>
                <p className="mt-1">{chapter7ResultExplanation}</p>
                <div className="mt-3 border-t border-gray-300 pt-3">
                  <OfficialLongFormLineHelp chapter="7" line="39c" /> Line 39c monthly disposable income: ${longFormMoney(calculation.line39c)}<br />
                  <OfficialLongFormLineHelp chapter="7" line="39d" /> Line 39d 60-month amount: ${longFormMoney(calculation.line39d)}<br />
                  <OfficialLongFormLineHelp chapter="7" line="41b" /> Line 41b 25% of nonpriority unsecured debt: ${longFormMoney(calculation.line41b)}
                  {!calculation.chapter7UsesDebtTest && ' (not used for the current line 40 result)'}
                  {calculation.chapter7UsesDebtTest && (
                    <><br /><OfficialLongFormLineHelp chapter="7" line="42" /> Line 42: {calculation.chapter7Line42Result === 'presumption' ? 'second box - presumption of abuse' : 'first box - no presumption of abuse'}</>
                  )}
                </div>
              </div>
            </div>
            <h4 className="mt-5 font-semibold text-gray-800">Line 43 - Part 4 special circumstances <OfficialLongFormLineHelp chapter="7" line="43" /></h4>
            {specialRows.map((row, index) => (
              <div key={index} className="mt-2 grid gap-2 md:grid-cols-[1fr_180px_auto]">
                <input value={row.description || ''} onChange={event => updateRow('special_circumstances', index, { description: event.target.value })} placeholder="Detailed explanation" className="rounded border border-gray-300 px-3 py-2" />
                <input type="number" min="0" step="0.01" value={row.amount ?? ''} onChange={event => updateRow('special_circumstances', index, { amount: event.target.value })} className="rounded border border-gray-300 px-3 py-2" />
                <button type="button" onClick={() => deleteRow('special_circumstances', index)} className="text-red-600">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => update('special_circumstances', [...specialRows, newSpecialCircumstance()])} className="mt-3 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700">Add special circumstance</button>
          </FormSection>
        ) : (
          <>
            <FormSection title="Chapter 13 disposable income - lines 39 through 45">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <MoneyInput chapter="13" line="40" label="Line 40 - Necessary dependent-child income" value={formData.dependent_child_income} onChange={value => update('dependent_child_income', value)} />
                <MoneyInput chapter="13" line="41" label="Line 41 - Retirement deductions and loan repayments" value={formData.retirement_contributions_and_loans} onChange={value => update('retirement_contributions_and_loans', value)} />
                <LineValue chapter="13" line="39"><strong>Line 39 current monthly income</strong><br />${longFormMoney(calculation.adjustedMonthlyIncome)} per month</LineValue>
                <LineValue chapter="13" line="42"><strong>Line 42 allowed deductions</strong><br />${longFormMoney(calculation.line38)} per month</LineValue>
                <LineValue chapter="13" line="44"><strong>Line 44 total adjustments</strong><br />${longFormMoney(calculation.chapter13Adjustments)} per month</LineValue>
                <LineValue chapter="13" line="45" tone="green"><strong>Line 45 disposable income</strong><br />${longFormMoney(calculation.chapter13DisposableIncome)} per month</LineValue>
              </div>
              <h4 className="mt-5 font-semibold text-gray-800">Line 43 special circumstances <OfficialLongFormLineHelp chapter="13" line="43" /></h4>
              {specialRows.map((row, index) => (
                <div key={index} className="mt-2 grid gap-2 md:grid-cols-[1fr_180px_auto]">
                  <input value={row.description || ''} onChange={event => updateRow('special_circumstances', index, { description: event.target.value })} placeholder="Special circumstance" className="rounded border border-gray-300 px-3 py-2" />
                  <input type="number" min="0" step="0.01" value={row.amount ?? ''} onChange={event => updateRow('special_circumstances', index, { amount: event.target.value })} className="rounded border border-gray-300 px-3 py-2" />
                  <button type="button" onClick={() => deleteRow('special_circumstances', index)} className="text-red-600">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => update('special_circumstances', [...specialRows, newSpecialCircumstance()])} className="mt-3 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700">Add special circumstance</button>
            </FormSection>

            <FormSection title="Part 3 - Changes in income or expenses">
              <p className="mb-3 text-sm text-gray-600">
                <OfficialLongFormLineHelp chapter="13" line="46" /> Line 46 - report each income or expense change that occurred or is virtually certain to occur while the case is open.
              </p>
              {changeRows.map((row, index) => (
                <div key={index} className="mb-3 grid gap-2 rounded-md border border-gray-200 p-3 md:grid-cols-6">
                  <select value={row.form || '122C-1'} onChange={event => updateRow('income_expense_changes', index, { form: event.target.value })} className="rounded border border-gray-300 px-2 py-2"><option value="122C-1">122C-1</option><option value="122C-2">122C-2</option></select>
                  <input value={row.line || ''} onChange={event => updateRow('income_expense_changes', index, { line: event.target.value })} placeholder="Line" className="rounded border border-gray-300 px-2 py-2" />
                  <input value={row.reason || ''} onChange={event => updateRow('income_expense_changes', index, { reason: event.target.value })} placeholder="Reason" className="rounded border border-gray-300 px-2 py-2 md:col-span-2" />
                  <input type="date" value={row.date || ''} onChange={event => updateRow('income_expense_changes', index, { date: event.target.value })} className="rounded border border-gray-300 px-2 py-2" />
                  <div className="flex gap-2">
                    <select value={row.direction || 'increase'} onChange={event => updateRow('income_expense_changes', index, { direction: event.target.value })} className="min-w-0 rounded border border-gray-300 px-2 py-2"><option value="increase">Increase</option><option value="decrease">Decrease</option></select>
                    <input type="number" min="0" step="0.01" value={row.amount ?? ''} onChange={event => updateRow('income_expense_changes', index, { amount: event.target.value })} className="w-28 rounded border border-gray-300 px-2 py-2" />
                  </div>
                  <button type="button" onClick={() => deleteRow('income_expense_changes', index)} className="text-left text-red-600 md:col-span-6">Remove change</button>
                </div>
              ))}
              <button type="button" onClick={() => update('income_expense_changes', [...changeRows, newIncomeExpenseChange()])} className="rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm text-indigo-700">Add change</button>
            </FormSection>
          </>
        )}
      </div>
      <div className="mt-6 flex justify-end border-t border-slate-200 pt-5">
        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="rounded-md bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save long form'}
        </button>
      </div>
    </div>
  );
}
